/**
 * What every live view says about the feed: whether what it shows is
 * last-known, and the one line that says so (twin slice 1, plan W3 steps
 * 11 and 12).
 *
 * ONE HOOK, read by the Overview map's heading (`LiveMapHeading`) and by
 * every building view's scrim (`useBuildingLiveInputs`). The two used to
 * hold a copy each of the same four lines -- the same FeedHealth, the same
 * site clock, the same minute tick -- and two copies of a sentence are how
 * the heading over the map and the scrim over the building come to say two
 * different things about one feed. The words themselves are
 * `feedHeadingWords`'.
 */
import { useFeedHealth } from '../../api/feed-health';
import { useMinuteTick } from '../../state/clock';
import { useActiveSite } from '../../state/useActiveSite';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { feedHeadingWords } from './live-map-heading';

export interface FeedHeading {
  /** `FeedHealth.clamped`: everything shown is last-known. A canvas greys
   *  (`desaturate`) and the heading's dot holds still. */
  readonly clamped: boolean;
  /** The line: "Reconnecting · last update 14:02". Null while live. */
  readonly words: string | null;
}

export function useFeedHeading(siteId: string): FeedHeading {
  const feed = useFeedHealth(siteId);
  const { sites } = useActiveSite();
  // Re-read on each minute boundary so "yesterday" turns over at midnight.
  const wallNowMs = useMinuteTick();
  const zone = resolveSiteTimeZone(sites, siteId);
  return {
    clamped: feed.clamped,
    words: feedHeadingWords(feed, zone.source === 'site' ? zone.timeZone : null, wallNowMs),
  };
}
